import React, { useState, useEffect } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function MessagesTab({ portalUser, projects = [], initialProjectId = '', onMessagesRead }) {
  const [selectedProject, setSelectedProject] = useState(projects[0]?._id || '');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (selectedProject) {
      fetchMessages();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (initialProjectId) {
      setSelectedProject(initialProjectId);
      return;
    }
    if (!selectedProject && projects.length) {
      setSelectedProject(projects[0]._id);
    }
  }, [initialProjectId, projects, selectedProject]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('clientToken');
      const res = await axios.get(`${API_URL}/messages/project/${selectedProject}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const list = res.data || [];
      setMessages(list);

      const unreadIncoming = list.filter((m) => !m.isRead && m.sender !== 'client');
      if (unreadIncoming.length) {
        await Promise.allSettled(
          unreadIncoming.map((m) =>
            axios.patch(
              `${API_URL}/messages/${m._id}/read`,
              {},
              { headers: { 'Authorization': `Bearer ${token}` } }
            )
          )
        );
        if (typeof onMessagesRead === 'function') onMessagesRead();
      }
      setError('');
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && !newFile) return;

    try {
      setSending(true);
      const token = localStorage.getItem('clientToken');
      const clientId = localStorage.getItem('clientId');

      let res;
      if (newFile) {
        const fd = new FormData();
        fd.append('file', newFile);
        fd.append('project', selectedProject);
        fd.append('client', clientId);
        fd.append('content', newMessage);
        res = await axios.post(`${API_URL}/messages`, fd, { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      } else {
        res = await axios.post(`${API_URL}/messages`, { project: selectedProject, client: clientId, content: newMessage }, { headers: { 'Authorization': `Bearer ${token}` } });
      }

      setMessages([...messages, res.data]);
      setNewMessage('');
      setNewFile(null);
    } catch (err) {
      alert('Failed to send message');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  if (projects.length === 0) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
        <MessageSquare size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
        No projects available for messaging
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '1rem' }}>
      {/* Project Selector */}
      <div>
        <label style={{ display: 'block', fontSize: '0.875rem', color: '#94a3b8', marginBottom: '0.5rem' }}>Select Project</label>
        <select 
          value={selectedProject} 
          onChange={(e) => setSelectedProject(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 1rem',
            background: 'rgba(15,23,42,0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.75rem',
            color: '#fff',
            outline: 'none'
          }}
        >
          {projects.map(project => (
            <option key={project._id} value={project._id}>{project.name}</option>
          ))}
        </select>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        background: 'rgba(30,41,59,0.3)',
        border: '1px solid rgba(255,255,255,0.05)',
        borderRadius: '1rem',
        padding: '1.5rem',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        minHeight: '300px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#94a3b8' }}>Loading messages...</div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#64748b', marginY: 'auto' }}>
            <MessageSquare size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
            No messages yet. Start a conversation!
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg._id}
              style={{
                display: 'flex',
                justifyContent: msg.sender === 'client' ? 'flex-end' : 'flex-start'
              }}
            >
              <div
                style={{
                  maxWidth: '70%',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.75rem',
                  background: msg.sender === 'client' ? 'linear-gradient(to right, #10b981, #06b6d4)' : 'rgba(15,23,42,0.5)',
                  color: msg.sender === 'client' ? '#020617' : '#fff',
                  wordWrap: 'break-word'
                }}
              >
                <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', opacity: 0.7 }}>
                  {msg.senderName} • {new Date(msg.createdAt).toLocaleTimeString()}
                </p>
                <p style={{ margin: 0 }}>{msg.content}</p>
                {msg.messageType === 'review_request' && msg.actionLink ? (
                  <div style={{ marginTop: '0.6rem' }}>
                    <a
                      href={msg.actionLink}
                      style={{
                        display: 'inline-block',
                        padding: '0.4rem 0.65rem',
                        borderRadius: '0.45rem',
                        textDecoration: 'none',
                        background: msg.sender === 'client' ? 'rgba(2,6,23,0.25)' : 'rgba(16,185,129,0.2)',
                        color: msg.sender === 'client' ? '#ffffff' : '#34d399',
                        border: msg.sender === 'client' ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(16,185,129,0.35)',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}
                    >
                      Open Review Form
                    </a>
                  </div>
                ) : null}
                {msg.attachment && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <a href={msg.attachment.signedUrl || msg.attachment.url} target="_blank" rel="noopener noreferrer" style={{ color: '#34d399' }}>{msg.attachment.originalName}</a>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            background: 'rgba(15,23,42,0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.75rem',
            color: '#fff',
            outline: 'none'
          }}
        />
        <button
          type="submit"
          disabled={!newMessage.trim() || sending}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(to right, #10b981, #06b6d4)',
            color: '#020617',
            border: 'none',
            borderRadius: '0.75rem',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            opacity: sending ? 0.5 : 1
          }}
        >
          <Send size={18} />
          {sending ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}
